import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';
import { resolveDeliveryLanguage } from '../utils/language';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const QUESTION_COUNT_OPTIONS = {
  demand:    [5, 10, 15, 20, 25, 30],
  unit_test: [5, 10, 15, 20, 25, 30],
  weekly:    [5, 10, 15, 20, 25, 30],
  monthly:   [5, 10, 15, 20, 25, 30],
  mid_year:  [5, 10, 15, 20, 25, 30],
  end_year:  [5, 10, 15, 20, 25, 30],
  challenge: [5, 10, 15, 20],
};

const EXAM_QUESTION_COUNT = {
  unit_test: 15,
  weekly:    20,
  monthly:   25,
  mid_year:  30,
  end_year:  30,
  challenge: 10,
  demand:    null,
};

const EXAM_DIFFICULTY_DIST = {
  unit_test: { easy: 0.20, medium: 0.40, hard: 0.30, challenge: 0.10 },
  weekly:    { easy: 0.15, medium: 0.35, hard: 0.35, challenge: 0.15 },
  monthly:   { easy: 0.10, medium: 0.30, hard: 0.40, challenge: 0.20 },
  mid_year:  { easy: 0.10, medium: 0.30, hard: 0.40, challenge: 0.20 },
  end_year:  { easy: 0.10, medium: 0.30, hard: 0.40, challenge: 0.20 },
  challenge: { easy: 0.00, medium: 0.20, hard: 0.50, challenge: 0.30 },
  demand:    null,
};

/* ─── i18n ──────────────────────────────────────────────────────────────── */
const T = {
  en: {
    title:          'My Quizzes',
    back:           '← Back',
    back_subjects:  '← Back to Subjects',
    toggle:         'AR',
    quiz_types_pill:'7 Quiz Types',
    no_quizzes:     'No quizzes available yet',
    fetch_error:    'Failed to load subjects',
    fail_load:      'Failed to load quiz',
    start_ut:       'Start',
    ready_since:    'Ready since',
    no_subjects:    'No subjects found',
    units_label:    'Select Units',
    count_label:    'Questions',
    diff_label:     'Difficulty',
    start:           'Start Quiz →',
    start_challenge:     'Start Challenge ⚡',
    challenge_note:      'Challenge questions are always hard level',
    show_answers:        'Show Answers',
    wrong_answers_title: 'Wrong Answers Review',
    perfect_score:       'Perfect score! No wrong answers.',
    your_answer:         'Your answer:',
    close:               'Close',
    min:                 'min',
    questions:           'Q',
    flexible:            'Flexible',
    anyTime:             'Any time',
    topics:              'topics',
    diff: [
      { id: 'easy',   label: 'Easy'   },
      { id: 'medium', label: 'Medium' },
      { id: 'hard',   label: 'Hard'   },
      { id: 'mixed',  label: 'Mixed'  },
    ],
    types: [
      { id: 'unit',      label: 'Unit Test',  icon: '📘', color: '#1D4ED8' },
      { id: 'weekly',    label: 'Weekly',     icon: '📅', color: '#7C3AED' },
      { id: 'monthly',   label: 'Monthly',    icon: '🏅', color: '#D97706' },
      { id: 'midterm',   label: 'Mid-Year',   icon: '📚', color: '#C2410C' },
      { id: 'final',     label: 'End-Year',   icon: '🎓', color: '#475569' },
      { id: 'challenge', label: 'Challenge',  icon: '⚡', color: '#DC2626' },
      { id: 'demand',    label: 'On-Demand',  icon: '🎯', color: '#0D9488' },
    ],
  },
  ar: {
    title:          'اختباراتي',
    back:           'رجوع →',
    back_subjects:  'رجوع للمواد →',
    toggle:         'EN',
    quiz_types_pill:'٧ أنواع اختبارات',
    no_quizzes:     'لا توجد اختبارات بعد',
    fetch_error:    'فشل تحميل المواد',
    fail_load:      'فشل تحميل الاختبار',
    start_ut:       'ابدأ',
    ready_since:    'جاهز منذ',
    no_subjects:    'لا توجد مواد',
    units_label:    'اختر الوحدات',
    count_label:    'عدد الأسئلة',
    diff_label:     'المستوى',
    start:           '→ ابدأ الاختبار',
    start_challenge:     '⚡ ابدأ التحدي',
    challenge_note:      'أسئلة التحدي دائماً من المستوى الصعب',
    show_answers:        'مراجعة الإجابات',
    wrong_answers_title: 'مراجعة الأخطاء',
    perfect_score:       '!ممتاز، كل إجاباتك صحيحة',
    your_answer:         ':إجابتك',
    close:               'إغلاق',
    min:                 'دقيقة',
    questions:           'سؤال',
    flexible:            'مرن',
    anyTime:             'أي وقت',
    topics:              'مواضيع',
    diff: [
      { id: 'easy',   label: 'سهل'   },
      { id: 'medium', label: 'متوسط' },
      { id: 'hard',   label: 'صعب'   },
      { id: 'mixed',  label: 'مختلط' },
    ],
    types: [
      { id: 'unit',      label: 'اختبار الوحدة',  icon: '📘', color: '#1D4ED8' },
      { id: 'weekly',    label: 'اختبار الأسبوع', icon: '📅', color: '#7C3AED' },
      { id: 'monthly',   label: 'اختبار الشهر',   icon: '🏅', color: '#D97706' },
      { id: 'midterm',   label: 'نصف العام',       icon: '📚', color: '#C2410C' },
      { id: 'final',     label: 'نهاية العام',     icon: '🎓', color: '#475569' },
      { id: 'challenge', label: 'تحدي',            icon: '⚡', color: '#DC2626' },
      { id: 'demand',    label: 'تحت الطلب',       icon: '🎯', color: '#0D9488' },
    ],
  },
};

/* ─── On-Demand inline form ─────────────────────────────────────────────── */
const DIFF_OPTIONS = [
  { id: 'easy',        en: 'Easy',          ar: 'سهل',          color: '#10B981' },
  { id: 'medium',      en: 'Medium',        ar: 'متوسط',         color: '#F59E0B' },
  { id: 'hard',        en: 'Hard',          ar: 'صعب',           color: '#EF4444' },
  { id: 'challenge',   en: 'Challenge',     ar: 'تحدي',          color: '#DC2626' },
  { id: 'easy_medium', en: 'Easy + Medium', ar: 'سهل + متوسط',   color: '#3B82F6' },
  { id: 'medium_hard', en: 'Med + Hard',    ar: 'متوسط + صعب',   color: '#8B5CF6' },
  { id: 'all',         en: 'All Mixed',     ar: 'مختلط',         color: '#059669' },
];

function OnDemandForm({ t, unitList, lang, subject, navigate, token }) {
  const [selectedUnits,        setSelectedUnits]        = useState([]);
  const [count,                setCount]                = useState(5);
  const [selectedDifficulties, setSelectedDifficulties] = useState(['all']);
  const [loading,              setLoading]              = useState(false);
  const [error,                setError]                = useState(null);
  const [builtCount,           setBuiltCount]           = useState(0);
  const [buildTotal,           setBuildTotal]           = useState(0);
  const [unitTopics,           setUnitTopics]           = useState({});
  // Per-unit selection — shape: { [unit_id]: string[] } — mirrors UnitTestCard
  const [selectedTopicsByUnit, setSelectedTopicsByUnit] = useState({});
  const [openUnits,            setOpenUnits]            = useState({});
  // Subject-driven content language (mirrors UnitTestCard / SubjectUnitsPage)
  const ARABIC_ONLY_SUBJECTS = ['التربية الإسلامية', 'اللغة العربية', 'تاريخ قطر والمواطنة'];
  const contentLang = ARABIC_ONLY_SUBJECTS.includes(subject?.subject_name) ? 'ar' : lang;

  function toggleUnit(id) {
    const isOn = !selectedUnits.includes(id);
    setSelectedUnits(prev => isOn ? [...prev, id] : prev.filter(x => x !== id));
    if (isOn) {
      const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
      studentFetch(`${API}/student/subjects/${studentData?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r ? r.json() : null)
        .then(data => {
          if (!data) return;
          const subj = (data.subjects || []).find(s => s.subject_id === subject.subject_id);
          const unit = (subj?.units || []).find(u => u.unit_id === id);
          setUnitTopics(prev => ({ ...prev, [id]: unit?.topics || [] }));
        })
        .catch(() => {});
    } else {
      setUnitTopics(prev => { const n = { ...prev }; delete n[id]; return n; });
      setSelectedTopicsByUnit(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }

  function toggleTopic(topicName, parentUnitId) {
    if (!parentUnitId) return;
    const currentForUnit = selectedTopicsByUnit[parentUnitId] || [];
    const isAdding = !currentForUnit.includes(topicName);
    setSelectedTopicsByUnit(prev => {
      const next = { ...prev };
      const list = next[parentUnitId] ? [...next[parentUnitId]] : [];
      if (isAdding) {
        if (!list.includes(topicName)) list.push(topicName);
        next[parentUnitId] = list;
      } else {
        const filtered = list.filter(x => x !== topicName);
        if (filtered.length === 0) delete next[parentUnitId];
        else next[parentUnitId] = filtered;
      }
      return next;
    });
    if (isAdding) {
      if (!selectedUnits.includes(parentUnitId)) {
        toggleUnit(parentUnitId);
      }
    } else {
      // After removal, if no topics remain selected for THIS unit, uncheck the unit
      const remainingForUnit = currentForUnit.filter(x => x !== topicName);
      if (remainingForUnit.length === 0) {
        setSelectedUnits(prev => prev.filter(x => x !== parentUnitId));
        setUnitTopics(prev => {
          const n = { ...prev }; delete n[parentUnitId]; return n;
        });
      }
    }
  }

  function toggleDifficulty(id) {
    setSelectedDifficulties(prev => {
      if (id === 'all') return ['all'];
      const withoutAll = prev.filter(x => x !== 'all');
      if (withoutAll.includes(id)) {
        const next = withoutAll.filter(x => x !== id);
        return next.length === 0 ? ['all'] : next;
      }
      return [...withoutAll, id];
    });
  }

  function buildDiffDist() {
    if (selectedDifficulties.includes('all'))         return { easy: 0.33, medium: 0.34, hard: 0.33 };
    if (selectedDifficulties.includes('easy_medium')) return { easy: 0.50, medium: 0.50 };
    if (selectedDifficulties.includes('medium_hard')) return { medium: 0.50, hard: 0.50 };
    const frac = 1 / selectedDifficulties.length;
    return Object.fromEntries(selectedDifficulties.map(d => [d, frac]));
  }

  function handleStart() {
    setLoading(true);
    setError(null);
    const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
    const storedStudent = JSON.parse(localStorage.getItem('student_data') || '{}');
    const sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    const unitNames = selectedUnits
      .map(uid => (unitList || []).find(u => u.unit_id === uid))
      .filter(Boolean)
      .map(u => u.unit_name)
      .join(', ');

    studentFetch(`${API}/session/build-quiz`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        student_id:              studentData?.id,
        session_id:              sessionId,
        subject:                 subject.subject_name,
        unit:                    unitNames,
        grade:                   studentData?.grade || storedStudent?.grade || 0,
        exam_type:               'demand',
        question_count:          count,
        student_selected_count:  true,
        difficulty_distribution: buildDiffDist(),
        delivery_language:       resolveDeliveryLanguage(subject, studentData),
        curriculum_type:         'cambridge',
        selected_topics:         (() => {
                                   const all = Object.values(selectedTopicsByUnit).flat();
                                   return all.length > 0 ? Array.from(new Set(all)) : null;
                                 })(),
        selected_topics_by_unit: Object.keys(selectedTopicsByUnit).length > 0 ? selectedTopicsByUnit : null,
        unit_id:                 selectedUnits.length === 1 ? selectedUnits[0] : null,
        unit_ids:                selectedUnits.length > 0 ? selectedUnits : null,
      }),
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          setError(err.message || 'حدث خطأ أثناء بناء الاختبار');
          setLoading(false);
          return;
        }
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) { setLoading(false); return; }
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();
            for (const part of parts) {
              const line = part.trim();
              if (!line.startsWith('data:')) continue;
              try {
                const msg = JSON.parse(line.slice(5).trim());
                if (msg.type === 'progress') {
                  setBuiltCount(msg.current);
                  setBuildTotal(msg.total);
                } else if (msg.type === 'complete') {
                  navigate('/student/quiz', {
                    state: {
                      student_id:        studentData?.id,
                      session_id:        sessionId,
                      subject:           subject.subject_name,
                      unit:              unitNames,
                      grade:             studentData?.grade || 0,
                      exam_type:         'demand',
                      questions:         msg.questions,
                      delivery_language: resolveDeliveryLanguage(subject, studentData),
                      subject_id:        subject.subject_id,
                      unit_id:           selectedUnits.length === 1 ? selectedUnits[0] : null,
                      return_to:         '/student/quizzes',
                      resume_navstate:   {},
                    },
                  });
                } else if (msg.type === 'error') {
                  setError(true);
                  setLoading(false);
                }
              } catch (_) {}
            }
            return pump();
          });
        }
        return pump();
      })
      .catch(() => { setError(true); setLoading(false); });
  }

  return (
    <div className="qh-od-form" style={{ position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
          borderRadius: 'inherit', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 10,
        }}>
          <div style={{
            background: 'white', borderRadius: 18, padding: '2rem 2.5rem',
            textAlign: 'center', width: '100%', maxWidth: 320,
            border: '1px solid #E5E7EB', boxShadow: '0 4px 20px rgba(5,150,105,.12)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎯</div>
            <div style={{ fontFamily: 'Sora,sans-serif', fontSize: '1rem', fontWeight: 800, color: '#1E3A5F', marginBottom: 6 }}>
              {lang === 'ar' ? 'جاري تجهيز اختبارك' : 'Building your quiz'}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#6B7280', marginBottom: '1.5rem' }}>
              {lang === 'ar' ? 'جهبذ بيبني الأسئلة خصيصاً ليك...' : 'Jahbath is crafting questions just for you...'}
            </div>
            <div style={{ background: '#F3F4F6', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{
                height: '100%', borderRadius: 100,
                background: 'linear-gradient(135deg,#059669,#10B981)',
                width: `${Math.round((builtCount / (buildTotal || count)) * 100)}%`,
                transition: 'width 0.4s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#6B7280' }}>
              <span>{lang === 'ar' ? `${builtCount} من ${buildTotal || count} أسئلة` : `${builtCount} of ${buildTotal || count} questions`}</span>
              <span>{lang === 'ar' ? 'لحظة من فضلك...' : 'Please wait...'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Units + Topics accordion */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif", letterSpacing: '.06em', marginBottom: 8 }}>
          {t.selectUnits || (lang === 'ar' ? 'اختر الوحدات والمواضيع' : 'Select Units & Topics')}
        </div>
        {(() => {
          const renderUnitRow = (u) => {
            const uid = u.unit_id || u.id;
            const isUnitSel = selectedUnits.includes(uid);
            const topicList = unitTopics[uid] || u.topics || [];
            const unitOpen = !!openUnits[uid];
            const unitSelectedTopics = selectedTopicsByUnit[uid] || [];
            const someTopicsSel = unitSelectedTopics.length > 0;
            return (
              <div key={uid} style={{ marginBottom: 6, background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' }}
                  onClick={() => {
                    if (!openUnits[uid] && !isUnitSel) toggleUnit(uid);
                    setOpenUnits(prev => ({ ...prev, [uid]: !prev[uid] }));
                  }}
                >
                  <div
                    onClick={e => { e.stopPropagation(); toggleUnit(uid); }}
                    style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      border: `2px solid ${isUnitSel ? '#0D9488' : '#D1D5DB'}`,
                      background: isUnitSel ? '#0D9488' : someTopicsSel ? '#CCFBF1' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                    }}
                  >
                    {isUnitSel && <span style={{ color: 'white', fontSize: 10, fontWeight: 800 }}>✓</span>}
                    {!isUnitSel && someTopicsSel && <span style={{ color: '#0D9488', fontSize: 12, fontWeight: 800, lineHeight: 1 }}>−</span>}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1E3A5F', fontFamily: "'Sora',sans-serif" }}>
                      {lang === 'ar' ? (u.unit_name_ar || u.unit_name) : u.unit_name}
                    </span>
                    {u.lesson_titles?.[u.semester] && (
                      <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 500, fontFamily: "'Sora',sans-serif", lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.lesson_titles[u.semester]}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif" }}>
                    {topicList.length} {lang === 'ar' ? 'مواضيع' : 'topics'}
                  </span>
                  <span style={{ color: '#D1D5DB', fontSize: 11, transition: '.2s', transform: unitOpen ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
                </div>
                {unitOpen && (
                  <div style={{ padding: '0 12px 8px', borderTop: '1px solid #F9FAFB' }}>
                    {topicList.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif", padding: '6px 4px' }}>
                        {lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}
                      </div>
                    ) : topicList.map((tp, ti) => {
                      const topicName = tp.topic_name || tp.name || tp;
                      // Selection identity stays on topic_name; only the visible string upgrades.
                      const topicDisplay = tp.topic_display ?? topicName;
                      const isSel = unitSelectedTopics.includes(topicName);
                      return (
                        <div
                          key={ti}
                          onClick={() => toggleTopic(topicName, uid)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 4px', cursor: 'pointer', borderRadius: 8 }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                            border: `2px solid ${isSel ? '#0D9488' : '#D1D5DB'}`,
                            background: isSel ? '#0D9488' : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {isSel && <span style={{ color: 'white', fontSize: 9, fontWeight: 800 }}>✓</span>}
                          </div>
                          <span style={{ fontSize: 12, fontFamily: "'Nunito',sans-serif", fontWeight: 700, color: isSel ? '#0D9488' : '#4B5563' }}>
                            {topicDisplay}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          };
          const isArabicSubject = ARABIC_ONLY_SUBJECTS.includes(subject?.subject_name);
          const hasSemesters = isArabicSubject && (unitList || []).some(u => u.semester != null);
          const unitsSem1 = hasSemesters ? unitList.filter(u => u.semester === 1) : (unitList || []);
          const unitsSem2 = hasSemesters ? unitList.filter(u => u.semester === 2) : [];
          const termHeaderStyle = { fontSize: 13, fontWeight: 700, color: '#1E3A5F', fontFamily: "'Sora',sans-serif", margin: '6px 0 8px 0' };
          if (!hasSemesters) return (unitList || []).map(renderUnitRow);
          return (
            <>
              <div style={termHeaderStyle}>{contentLang === 'ar' ? '📚 الفصل الأول' : '📚 Term 1'}</div>
              {unitsSem1.map(renderUnitRow)}
              {unitsSem2.length > 0 && (
                <>
                  <div style={{ ...termHeaderStyle, marginTop: 14 }}>{contentLang === 'ar' ? '📚 الفصل الثاني' : '📚 Term 2'}</div>
                  {unitsSem2.map(renderUnitRow)}
                </>
              )}
            </>
          );
        })()}
      </div>

      <div className="qh-od-field">
        <span className="qh-od-label">{t.count_label}</span>
        <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'8px'}}>
          {QUESTION_COUNT_OPTIONS['demand'].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => setCount(n)}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: count === n ? '2px solid #059669' : '1px solid #D1D5DB',
                background: count === n ? '#F0FDF4' : 'white',
                color: count === n ? '#059669' : '#374151',
                fontWeight: count === n ? '600' : '400',
                cursor: 'pointer',
                fontSize: '15px',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="qh-od-field">
        <span className="qh-od-label">{t.diff_label}</span>
        <div className="qh-diff-row">
          {DIFF_OPTIONS.map(d => {
            const selected = selectedDifficulties.includes(d.id);
            return (
              <button
                key={d.id}
                className="qh-diff-btn"
                style={selected
                  ? { background: d.color, borderColor: d.color, color: 'white' }
                  : { borderColor: d.color, color: d.color }
                }
                onClick={() => toggleDifficulty(d.id)}
              >
                {lang === 'ar' ? d.ar : d.en}
              </button>
            );
          })}
        </div>
      </div>

      {error && <p className="qh-ut-error">{t.fail_load}</p>}

      {loading && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ textAlign: 'center', fontSize: 14, marginBottom: 6, direction: 'rtl' }}>
            {lang === 'ar' ? `جارى تحضير السؤال ${builtCount} من ${buildTotal || count}` : `Building question ${builtCount} of ${buildTotal || count}`}
          </p>
          <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#6366F1', borderRadius: 4, width: `${(buildTotal || count) > 0 ? Math.round((builtCount / (buildTotal || count)) * 100) : 0}%`, transition: 'width 0.3s ease' }} />
          </div>
        </div>
      )}
      <button
        className="qh-start-btn"
        disabled={selectedUnits.length === 0 || selectedDifficulties.length === 0 || loading}
        onClick={handleStart}
      >
        {loading ? (lang === 'ar' ? 'جارى التجهيز...' : 'Building...') : t.start}
      </button>
    </div>
  );
}

/* ─── Unit Test quiz card ────────────────────────────────────────────────── */
const EXAM_LABELS = {
  unit_test: { en: 'Unit Test', ar: 'اختبار الوحدة'  },
  weekly:    { en: 'Weekly',    ar: 'اختبار الأسبوع' },
  monthly:   { en: 'Monthly',   ar: 'اختبار الشهر'   },
  mid_year:  { en: 'Mid-Year',  ar: 'نصف العام'       },
  end_year:  { en: 'End-Year',  ar: 'نهاية العام'     },
};

function UnitTestCard({ quiz, subject, token, lang, t, navigate, unitList, noCustomPanel = false }) {
  const isCustomOnly = !quiz.id;
  const LOCKED_DIFFICULTY_TYPES = [
    'unit_test', 'weekly', 'monthly', 'mid_year', 'end_year', 'challenge'
  ];
  const isDifficultyLocked = LOCKED_DIFFICULTY_TYPES.includes(quiz?.exam_type);
  // Subject-driven content language (mirrors SubjectUnitsPage 117-123) — used for
  // labels that should follow the subject's language, not the UI toggle.
  const ARABIC_ONLY_SUBJECTS = ['التربية الإسلامية', 'اللغة العربية', 'تاريخ قطر والمواطنة'];
  const contentLang = ARABIC_ONLY_SUBJECTS.includes(subject?.subject_name) ? 'ar' : lang;
  const [loading,              setLoading]              = useState(false);
  const [error,                setError]                = useState(null);
  const [showCustom,           setShowCustom]           = useState(!quiz.id);
  const [customUnits,          setCustomUnits]          = useState([]);
  const [selectedCustomUnits,  setSelectedCustomUnits]  = useState([]);
  const [customTopics,         setCustomTopics]         = useState({});
  // Per-unit selection — shape: { [unit_id]: string[] } — mirrors SubjectUnitsPage
  const [selectedCustomTopicsByUnit, setSelectedCustomTopicsByUnit] = useState({});
  const [customCount,          setCustomCount]          = useState(5);
  const [customDiffs,          setCustomDiffs]          = useState(['all']);
  const [customLoading,        setCustomLoading]        = useState(false);
  const [customBuiltCount,     setCustomBuiltCount]     = useState(0);
  const [customBuildTotal,     setCustomBuildTotal]     = useState(0);
  const [openUnits,            setOpenUnits]            = useState({});

  useEffect(() => {
    const fixed = EXAM_QUESTION_COUNT[quiz?.exam_type];
    if (fixed) setCustomCount(fixed);
  }, [quiz?.exam_type]);

  useEffect(() => { setCustomUnits(unitList || []); }, []);

  function toggleCustomUnit(id) {
    const isOn = !selectedCustomUnits.includes(id);
    setSelectedCustomUnits(prev => isOn ? [...prev, id] : prev.filter(x => x !== id));
    if (isOn) {
      const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
      studentFetch(`${API}/student/subjects/${studentData?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r ? r.json() : null)
        .then(data => {
          if (!data) return;
          const subj = (data.subjects || []).find(s => s.subject_id === subject.subject_id);
          const unit = (subj?.units || []).find(u => u.unit_id === id);
          setCustomTopics(prev => ({ ...prev, [id]: unit?.topics || [] }));
        })
        .catch(() => {});
    } else {
      setCustomTopics(prev => { const n = { ...prev }; delete n[id]; return n; });
      setSelectedCustomTopicsByUnit(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }

  function toggleCustomTopic(topicName, parentUnitId) {
    if (!parentUnitId) return;
    const currentForUnit = selectedCustomTopicsByUnit[parentUnitId] || [];
    const isAdding = !currentForUnit.includes(topicName);
    setSelectedCustomTopicsByUnit(prev => {
      const next = { ...prev };
      const list = next[parentUnitId] ? [...next[parentUnitId]] : [];
      if (isAdding) {
        if (!list.includes(topicName)) list.push(topicName);
        next[parentUnitId] = list;
      } else {
        const filtered = list.filter(x => x !== topicName);
        if (filtered.length === 0) delete next[parentUnitId];
        else next[parentUnitId] = filtered;
      }
      return next;
    });
    if (isAdding) {
      if (!selectedCustomUnits.includes(parentUnitId)) {
        setSelectedCustomUnits(prev => [...prev, parentUnitId]);
        const studentData = (() => {
          try { return JSON.parse(localStorage.getItem('student_data')); }
          catch { return {}; }
        })();
        studentFetch(`${API}/student/subjects/${studentData?.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => r ? r.json() : null)
          .then(data => {
            if (!data) return;
            const subj = (data.subjects || []).find(
              s => s.subject_id === subject.subject_id
            );
            const unit = (subj?.units || []).find(
              u => u.unit_id === parentUnitId
            );
            setCustomTopics(prev => ({
              ...prev,
              [parentUnitId]: unit?.topics || [],
            }));
          })
          .catch(() => {});
      }
    } else {
      // After removal, if no topics remain selected for THIS unit, uncheck the unit
      const remainingForUnit = currentForUnit.filter(x => x !== topicName);
      if (remainingForUnit.length === 0) {
        setSelectedCustomUnits(prev =>
          prev.filter(x => x !== parentUnitId)
        );
        setCustomTopics(prev => {
          const n = { ...prev };
          delete n[parentUnitId];
          return n;
        });
      }
    }
  }

  function toggleCustomDiff(id) {
    setCustomDiffs(prev => {
      if (id === 'all') return ['all'];
      const withoutAll = prev.filter(x => x !== 'all');
      if (withoutAll.includes(id)) {
        const next = withoutAll.filter(x => x !== id);
        return next.length === 0 ? ['all'] : next;
      }
      return [...withoutAll, id];
    });
  }

  function buildCustomDiffDist() {
    if (customDiffs.includes('all'))         return { easy: 0.33, medium: 0.34, hard: 0.33 };
    if (customDiffs.includes('easy_medium')) return { easy: 0.50, medium: 0.50 };
    if (customDiffs.includes('medium_hard')) return { medium: 0.50, hard: 0.50 };
    const frac = 1 / customDiffs.length;
    return Object.fromEntries(customDiffs.map(d => [d, frac]));
  }

  function handleCustomStart() {
    setCustomLoading(true);
    const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
    const storedStudent = JSON.parse(localStorage.getItem('student_data') || '{}');
    const sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    const unitNames = selectedCustomUnits
      .map(uid => (customUnits || []).find(u => u.unit_id === uid))
      .filter(Boolean)
      .map(u => u.unit_name)
      .join(', ');
    studentFetch(`${API}/session/build-quiz`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        student_id:              studentData?.id,
        session_id:              sessionId,
        subject:                 subject.subject_name,
        unit:                    unitNames,
        grade:                   studentData?.grade || storedStudent?.grade || 0,
        exam_type:               quiz.exam_type,
        question_count:          EXAM_QUESTION_COUNT[quiz?.exam_type] ?? customCount,
        student_selected_count:  true,
        difficulty_distribution: EXAM_DIFFICULTY_DIST[quiz?.exam_type] ?? buildCustomDiffDist(),
        delivery_language:       resolveDeliveryLanguage(subject, studentData),
        curriculum_type:         'cambridge',
        selected_topics:         (() => {
                                   const all = Object.values(selectedCustomTopicsByUnit).flat();
                                   return all.length > 0 ? Array.from(new Set(all)) : null;
                                 })(),
        selected_topics_by_unit: Object.keys(selectedCustomTopicsByUnit).length > 0 ? selectedCustomTopicsByUnit : null,
        unit_id:                 selectedCustomUnits.length === 1 ? selectedCustomUnits[0] : null,
        unit_ids:                selectedCustomUnits.length > 0 ? selectedCustomUnits : null,
      }),
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          setError(err.message || 'حدث خطأ أثناء بناء الاختبار');
          setCustomLoading(false);
          return;
        }
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) { setCustomLoading(false); return; }
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();
            for (const part of parts) {
              const line = part.trim();
              if (!line.startsWith('data:')) continue;
              try {
                const msg = JSON.parse(line.slice(5).trim());
                if (msg.type === 'progress') {
                  setCustomBuiltCount(msg.current);
                  setCustomBuildTotal(msg.total);
                } else if (msg.type === 'complete') {
                  navigate('/student/quiz', {
                    state: {
                      student_id:        studentData?.id,
                      session_id:        sessionId,
                      subject:           subject.subject_name,
                      unit:              unitNames,
                      grade:             studentData?.grade || 0,
                      exam_type:         quiz.exam_type,
                      questions:         msg.questions,
                      delivery_language: resolveDeliveryLanguage(subject, studentData),
                      subject_id:        subject.subject_id,
                      unit_id:           selectedCustomUnits.length === 1 ? selectedCustomUnits[0] : null,
                      return_to:         '/student/quizzes',
                      resume_navstate:   {},
                    },
                  });
                } else if (msg.type === 'error') {
                  setCustomLoading(false);
                }
              } catch (_) {}
            }
            return pump();
          });
        }
        return pump();
      })
      .catch(() => { setCustomLoading(false); });
  }

  function handleStart() {
    setLoading(true);
    setError(null);
    const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
    studentFetch(`${API}/student/quizzes/${quiz.id}/questions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.success && data.questions) {
          navigate('/student/quiz', {
            state: {
              student_id:        studentData?.id,
              session_id:        quiz.id,
              subject:           subject.subject_name,
              unit:              quiz.unit_id,
              grade:             studentData?.grade,
              exam_type:         quiz.exam_type,
              questions:         data.questions,
              delivery_language: resolveDeliveryLanguage(subject, studentData),
              subject_id:        subject.subject_id,
              return_to:         '/student/quizzes',
              resume_navstate:   {},
            },
          });
        } else {
          setError(true);
          setLoading(false);
        }
      })
      .catch(() => { setError(true); setLoading(false); });
  }

  const createdDate = quiz.created_at
    ? new Date(quiz.created_at).toLocaleDateString()
    : '';
  const qCount = lang === 'ar'
    ? `${quiz.question_count} سؤال`
    : `${quiz.question_count} Questions`;
  const examLabel = EXAM_LABELS[quiz.exam_type] || EXAM_LABELS.unit_test;
  const unitName = (subject.units || []).find(u => u.unit_id === quiz.unit_id)?.unit_name
    || (lang === 'ar' ? examLabel.ar : examLabel.en);

  return (
    <>
      <div className="qh-ut-card">
        {!isCustomOnly && (
          <>
            <div className="qh-ut-info">
              <span className="qh-ut-unit">
                {unitName}
              </span>
              <span className="qh-ut-meta">
                {t.ready_since} {createdDate} · {qCount}
              </span>
            </div>
            {quiz.status === 'completed'
              ? <span className="qh-ut-done">{lang === 'ar' ? '✓ مكتمل' : '✓ Completed'}</span>
              : (
                <button
                  className="qh-ut-start"
                  onClick={handleStart}
                  disabled={loading}
                >
                  {loading ? '…' : t.start_ut}
                </button>
              )
            }
            {error && (
              <p className="qh-ut-error">{t.fail_load}</p>
            )}
          </>
        )}
        {!isCustomOnly && !noCustomPanel && (
          <button className="qh-ut-custom-btn" onClick={() => setShowCustom(p => !p)}>
            {showCustom ? (lang === 'ar' ? '× إغلاق' : '× Close') : (lang === 'ar' ? '+ بناء اختبار مخصص' : '+ Build custom quiz')}
          </button>
        )}
      </div>

      {!noCustomPanel && showCustom && (
        <div className="qh-custom-wrap" style={{ position: 'relative' }}>
          {customLoading && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
              borderRadius: 'inherit', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 10,
            }}>
              <div style={{
                background: 'white', borderRadius: 18, padding: '2rem 2.5rem',
                textAlign: 'center', width: '100%', maxWidth: 320,
                border: '1px solid #E5E7EB', boxShadow: '0 4px 20px rgba(29,78,216,.12)',
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📘</div>
                <div style={{ fontFamily: 'Sora,sans-serif', fontSize: '1rem', fontWeight: 800, color: '#1E3A5F', marginBottom: 6 }}>
                  {lang === 'ar' ? 'جاري تجهيز اختبارك' : 'Building your quiz'}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#6B7280', marginBottom: '1.5rem' }}>
                  {lang === 'ar' ? 'جهبذ بيبني الأسئلة خصيصاً ليك...' : 'Jahbath is crafting questions just for you...'}
                </div>
                <div style={{ background: '#F3F4F6', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{
                    height: '100%', borderRadius: 100,
                    background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)',
                    width: `${Math.round((customBuiltCount / customCount) * 100)}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#6B7280' }}>
                  <span>{lang === 'ar' ? `${customBuiltCount} من ${customCount} أسئلة` : `${customBuiltCount} of ${customCount} questions`}</span>
                  <span>{lang === 'ar' ? 'لحظة من فضلك...' : 'Please wait...'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Units accordion */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif", letterSpacing: '.06em', marginBottom: 8 }}>
              {t.selectUnits || 'اختر الوحدات والمواضيع'}
            </div>
            {(() => {
              const renderUnitRow = (u) => {
                const uid = u.unit_id || u.id;
                const isUnitSel = selectedCustomUnits.includes(uid);
                const unitTopicList = customTopics?.[uid] || u.topics || [];
                const unitOpen = !!openUnits[uid];
                const unitSelectedTopics = selectedCustomTopicsByUnit[uid] || [];
                const someTopicsSel = unitSelectedTopics.length > 0;
                return (
                  <div key={uid} style={{ marginBottom: 6, background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
                    {/* Unit row */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' }}
                      onClick={() => setOpenUnits(prev => ({ ...prev, [uid]: !prev[uid] }))}
                    >
                      {/* Unit checkbox */}
                      <div
                        onClick={e => { e.stopPropagation(); toggleCustomUnit(uid); }}
                        style={{
                          width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                          border: `2px solid ${isUnitSel ? '#059669' : '#D1D5DB'}`,
                          background: isUnitSel ? '#059669' : someTopicsSel ? '#D1FAE5' : 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                        }}
                      >
                        {isUnitSel && <span style={{ color: 'white', fontSize: 10, fontWeight: 800 }}>✓</span>}
                        {!isUnitSel && someTopicsSel && <span style={{ color: '#059669', fontSize: 12, fontWeight: 800, lineHeight: 1 }}>−</span>}
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1E3A5F', fontFamily: "'Sora',sans-serif" }}>
                          {lang === 'ar' ? (u.unit_name_ar || u.unit_name) : u.unit_name}
                        </span>
                        {u.lesson_titles?.[u.semester] && (
                          <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 500, fontFamily: "'Sora',sans-serif", lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {u.lesson_titles[u.semester]}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif" }}>
                        {unitTopicList.length} {t.topics || 'مواضيع'}
                      </span>
                      <span style={{ color: '#D1D5DB', fontSize: 11, transition: '.2s', transform: unitOpen ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
                    </div>
                    {/* Topics list */}
                    {unitOpen && unitTopicList.length > 0 && (
                      <div style={{ padding: '0 12px 8px', borderTop: '1px solid #F9FAFB' }}>
                        {unitTopicList.map((tp, ti) => {
                          const topicName = tp.topic_name || tp.name || tp;
                          // Selection identity stays on topic_name; only the visible string upgrades.
                          const topicDisplay = tp.topic_display ?? topicName;
                          const isSel = unitSelectedTopics.includes(topicName);
                          return (
                            <div
                              key={ti}
                              onClick={() => toggleCustomTopic(topicName, uid)}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 4px', cursor: 'pointer', borderRadius: 8 }}
                            >
                              <div style={{
                                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                border: `2px solid ${isSel ? '#059669' : '#D1D5DB'}`,
                                background: isSel ? '#059669' : 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}>
                                {isSel && <span style={{ color: 'white', fontSize: 9, fontWeight: 800 }}>✓</span>}
                              </div>
                              <span style={{ fontSize: 12, fontFamily: "'Nunito',sans-serif", fontWeight: 700, color: isSel ? '#059669' : '#4B5563' }}>
                                {topicDisplay}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              };
              const isArabicSubject = ARABIC_ONLY_SUBJECTS.includes(subject?.subject_name);
              const hasSemesters = isArabicSubject && (customUnits || []).some(u => u.semester != null);
              const unitsSem1 = hasSemesters ? customUnits.filter(u => u.semester === 1) : (customUnits || []);
              const unitsSem2 = hasSemesters ? customUnits.filter(u => u.semester === 2) : [];
              const termHeaderStyle = { fontSize: 13, fontWeight: 700, color: '#1E3A5F', fontFamily: "'Sora',sans-serif", margin: '6px 0 8px 0' };
              if (!hasSemesters) return (customUnits || []).map(renderUnitRow);
              return (
                <>
                  <div style={termHeaderStyle}>{contentLang === 'ar' ? '📚 الفصل الأول' : '📚 Term 1'}</div>
                  {unitsSem1.map(renderUnitRow)}
                  {unitsSem2.length > 0 && (
                    <>
                      <div style={{ ...termHeaderStyle, marginTop: 14 }}>{contentLang === 'ar' ? '📚 الفصل الثاني' : '📚 Term 2'}</div>
                      {unitsSem2.map(renderUnitRow)}
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {quiz?.exam_type === 'demand' && (
          <div className="qh-od-field">
            <span className="qh-od-label">{t.count_label}</span>
            <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'8px'}}>
              {(QUESTION_COUNT_OPTIONS[quiz?.exam_type] ?? QUESTION_COUNT_OPTIONS['unit_test']).map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCustomCount(n)}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '8px',
                    border: customCount === n ? '2px solid #059669' : '1px solid #D1D5DB',
                    background: customCount === n ? '#F0FDF4' : 'white',
                    color: customCount === n ? '#059669' : '#374151',
                    fontWeight: customCount === n ? '600' : '400',
                    cursor: 'pointer',
                    fontSize: '15px',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          )}

          {!isDifficultyLocked && (
          <div className="qh-od-field">
            <span className="qh-od-label">{t.diff_label}</span>
            <div className="qh-diff-row">
              {DIFF_OPTIONS.map(d => {
                const selected = customDiffs.includes(d.id);
                return (
                  <button
                    key={d.id}
                    className="qh-diff-btn"
                    style={selected
                      ? { background: d.color, borderColor: d.color, color: 'white' }
                      : { borderColor: d.color, color: d.color }
                    }
                    onClick={() => toggleCustomDiff(d.id)}
                  >
                    {lang === 'ar' ? d.ar : d.en}
                  </button>
                );
              })}
            </div>
          </div>
          )}
          {isDifficultyLocked && (
            <div style={{
              fontSize: 13,
              color: '#6B7280',
              padding: '8px 12px',
              background: '#F9FAFB',
              borderRadius: 8,
              marginBottom: 12,
            }}>
              {quiz?.exam_type === 'mid_year' || quiz?.exam_type === 'end_year'
                ? '🎓 Difficulty is set automatically for this exam'
                : '📅 Difficulty is set automatically for monthly exams'}
            </div>
          )}

          {customLoading && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ textAlign: 'center', fontSize: 14, marginBottom: 6, direction: 'rtl' }}>
                {lang === 'ar' ? `جارى تحضير السؤال ${customBuiltCount} من ${customBuildTotal || customCount}` : `Building question ${customBuiltCount} of ${customBuildTotal || customCount}`}
              </p>
              <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#6366F1', borderRadius: 4, width: `${customCount > 0 ? Math.round((customBuiltCount / customCount) * 100) : 0}%`, transition: 'width 0.3s ease' }} />
              </div>
            </div>
          )}
          <button
            className="qh-start-btn"
            disabled={
              (selectedCustomUnits.length === 0 && Object.keys(selectedCustomTopicsByUnit).length === 0) ||
              (quiz?.exam_type === 'demand' && customDiffs.length === 0) ||
              customLoading
            }
            onClick={handleCustomStart}
          >
            {customLoading ? (lang === 'ar' ? 'جارى التجهيز...' : 'Building...') : t.start}
          </button>
        </div>
      )}
    </>
  );
}

/* ─── Challenge inline form ─────────────────────────────────────────────── */
function ChallengeForm({ t, unitList, lang, subject, navigate, token }) {
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [count,  setCount]  = useState(5);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [builtCount,     setBuiltCount]     = useState(0);
  const [buildTotal,     setBuildTotal]     = useState(0);
  const [unitTopics,     setUnitTopics]     = useState({});
  // Per-unit selection — shape: { [unit_id]: string[] } — mirrors UnitTestCard
  const [selectedTopicsByUnit, setSelectedTopicsByUnit] = useState({});
  const [openUnits,      setOpenUnits]      = useState({});
  // Subject-driven content language (mirrors UnitTestCard / SubjectUnitsPage)
  const ARABIC_ONLY_SUBJECTS = ['التربية الإسلامية', 'اللغة العربية', 'تاريخ قطر والمواطنة'];
  const contentLang = ARABIC_ONLY_SUBJECTS.includes(subject?.subject_name) ? 'ar' : lang;

  function toggleUnit(id) {
    const isOn = !selectedUnits.includes(id);
    setSelectedUnits(prev => isOn ? [...prev, id] : prev.filter(x => x !== id));
    if (isOn) {
      const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
      studentFetch(`${API}/student/subjects/${studentData?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r ? r.json() : null)
        .then(data => {
          if (!data) return;
          const subj = (data.subjects || []).find(s => s.subject_id === subject.subject_id);
          const unit = (subj?.units || []).find(u => u.unit_id === id);
          setUnitTopics(prev => ({ ...prev, [id]: unit?.topics || [] }));
        })
        .catch(() => {});
    } else {
      setUnitTopics(prev => { const n = { ...prev }; delete n[id]; return n; });
      setSelectedTopicsByUnit(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }

  function toggleTopic(topicName, parentUnitId) {
    if (!parentUnitId) return;
    const currentForUnit = selectedTopicsByUnit[parentUnitId] || [];
    const isAdding = !currentForUnit.includes(topicName);
    setSelectedTopicsByUnit(prev => {
      const next = { ...prev };
      const list = next[parentUnitId] ? [...next[parentUnitId]] : [];
      if (isAdding) {
        if (!list.includes(topicName)) list.push(topicName);
        next[parentUnitId] = list;
      } else {
        const filtered = list.filter(x => x !== topicName);
        if (filtered.length === 0) delete next[parentUnitId];
        else next[parentUnitId] = filtered;
      }
      return next;
    });
    if (isAdding) {
      if (!selectedUnits.includes(parentUnitId)) {
        toggleUnit(parentUnitId);
      }
    } else {
      // After removal, if no topics remain selected for THIS unit, uncheck the unit
      const remainingForUnit = currentForUnit.filter(x => x !== topicName);
      if (remainingForUnit.length === 0) {
        setSelectedUnits(prev => prev.filter(x => x !== parentUnitId));
        setUnitTopics(prev => {
          const n = { ...prev }; delete n[parentUnitId]; return n;
        });
      }
    }
  }

  function handleStart() {
    setLoading(true);
    setError(null);
    const studentData = (() => { try { return JSON.parse(localStorage.getItem('student_data')); } catch { return {}; } })();
    const storedStudent = JSON.parse(localStorage.getItem('student_data') || '{}');
    const sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    const unitNames = selectedUnits
      .map(uid => (unitList || []).find(u => u.unit_id === uid))
      .filter(Boolean)
      .map(u => u.unit_name)
      .join(', ');

    studentFetch(`${API}/session/build-quiz`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        student_id:              studentData?.id,
        session_id:              sessionId,
        subject:                 subject.subject_name,
        unit:                    unitNames,
        grade:                   studentData?.grade || storedStudent?.grade || 0,
        exam_type:               'challenge',
        question_count:          EXAM_QUESTION_COUNT['challenge'],
        student_selected_count:  true,
        difficulty_distribution: { hard: 0.50, challenge: 0.50 },
        delivery_language:       resolveDeliveryLanguage(subject, studentData),
        curriculum_type:         'cambridge',
        selected_topics:         (() => {
                                   const all = Object.values(selectedTopicsByUnit).flat();
                                   return all.length > 0 ? Array.from(new Set(all)) : null;
                                 })(),
        selected_topics_by_unit: Object.keys(selectedTopicsByUnit).length > 0 ? selectedTopicsByUnit : null,
        unit_id:                 selectedUnits.length === 1 ? selectedUnits[0] : null,
        unit_ids:                selectedUnits.length > 0 ? selectedUnits : null,
      }),
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          setError(err.message || 'حدث خطأ أثناء بناء الاختبار');
          setLoading(false);
          return;
        }
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) { setLoading(false); return; }
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();
            for (const part of parts) {
              const line = part.trim();
              if (!line.startsWith('data:')) continue;
              try {
                const msg = JSON.parse(line.slice(5).trim());
                if (msg.type === 'progress') {
                  setBuiltCount(msg.current);
                  setBuildTotal(msg.total);
                } else if (msg.type === 'complete') {
                  navigate('/student/quiz', {
                    state: {
                      student_id:        studentData?.id,
                      session_id:        sessionId,
                      subject:           subject.subject_name,
                      unit:              unitNames,
                      grade:             studentData?.grade || 0,
                      exam_type:         'challenge',
                      questions:         msg.questions,
                      delivery_language: resolveDeliveryLanguage(subject, studentData),
                      subject_id:        subject.subject_id,
                      unit_id:           selectedUnits.length === 1 ? selectedUnits[0] : null,
                      return_to:         '/student/quizzes',
                      resume_navstate:   {},
                    },
                  });
                } else if (msg.type === 'error') {
                  setError(true);
                  setLoading(false);
                }
              } catch (_) {}
            }
            return pump();
          });
        }
        return pump();
      })
      .catch(() => { setError(true); setLoading(false); });
  }

  return (
    <div className="qh-od-form" style={{ position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
          borderRadius: 'inherit', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 10,
        }}>
          <div style={{
            background: 'white', borderRadius: 18, padding: '2rem 2.5rem',
            textAlign: 'center', width: '100%', maxWidth: 320,
            border: '1px solid #E5E7EB', boxShadow: '0 4px 20px rgba(220,38,38,.12)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚡</div>
            <div style={{ fontFamily: 'Sora,sans-serif', fontSize: '1rem', fontWeight: 800, color: '#1E3A5F', marginBottom: 6 }}>
              {lang === 'ar' ? 'جاري تجهيز اختبارك' : 'Building your quiz'}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#6B7280', marginBottom: '1.5rem' }}>
              {lang === 'ar' ? 'جهبذ بيبني الأسئلة خصيصاً ليك...' : 'Jahbath is crafting questions just for you...'}
            </div>
            <div style={{ background: '#F3F4F6', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{
                height: '100%', borderRadius: 100,
                background: 'linear-gradient(135deg,#DC2626,#EF4444)',
                width: `${Math.round((builtCount / (buildTotal || count)) * 100)}%`,
                transition: 'width 0.4s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#6B7280' }}>
              <span>{lang === 'ar' ? `${builtCount} من ${buildTotal || count} أسئلة` : `${builtCount} of ${buildTotal || count} questions`}</span>
              <span>{lang === 'ar' ? 'لحظة من فضلك...' : 'Please wait...'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Units + Topics accordion */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif", letterSpacing: '.06em', marginBottom: 8 }}>
          {t.selectUnits || (lang === 'ar' ? 'اختر الوحدات والمواضيع' : 'Select Units & Topics')}
        </div>
        {(() => {
          const renderUnitRow = (u) => {
            const uid = u.unit_id || u.id;
            const isUnitSel = selectedUnits.includes(uid);
            const topicList = unitTopics[uid] || u.topics || [];
            const unitOpen = !!openUnits[uid];
            const unitSelectedTopics = selectedTopicsByUnit[uid] || [];
            const someTopicsSel = unitSelectedTopics.length > 0;
            return (
              <div key={uid} style={{ marginBottom: 6, background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' }}
                  onClick={() => {
                    if (!openUnits[uid] && !isUnitSel) toggleUnit(uid);
                    setOpenUnits(prev => ({ ...prev, [uid]: !prev[uid] }));
                  }}
                >
                  <div
                    onClick={e => { e.stopPropagation(); toggleUnit(uid); }}
                    style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      border: `2px solid ${isUnitSel ? '#DC2626' : '#D1D5DB'}`,
                      background: isUnitSel ? '#DC2626' : someTopicsSel ? '#FECACA' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                    }}
                  >
                    {isUnitSel && <span style={{ color: 'white', fontSize: 10, fontWeight: 800 }}>✓</span>}
                    {!isUnitSel && someTopicsSel && <span style={{ color: '#DC2626', fontSize: 12, fontWeight: 800, lineHeight: 1 }}>−</span>}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1E3A5F', fontFamily: "'Sora',sans-serif" }}>
                      {lang === 'ar' ? (u.unit_name_ar || u.unit_name) : u.unit_name}
                    </span>
                    {u.lesson_titles?.[u.semester] && (
                      <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 500, fontFamily: "'Sora',sans-serif", lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.lesson_titles[u.semester]}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif" }}>
                    {topicList.length} {lang === 'ar' ? 'مواضيع' : 'topics'}
                  </span>
                  <span style={{ color: '#D1D5DB', fontSize: 11, transition: '.2s', transform: unitOpen ? 'rotate(180deg)' : 'none', display: 'inline-block' }}>▼</span>
                </div>
                {unitOpen && (
                  <div style={{ padding: '0 12px 8px', borderTop: '1px solid #F9FAFB' }}>
                    {topicList.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif", padding: '6px 4px' }}>
                        {lang === 'ar' ? 'جارٍ التحميل...' : 'Loading...'}
                      </div>
                    ) : topicList.map((tp, ti) => {
                      const topicName = tp.topic_name || tp.name || tp;
                      // Selection identity stays on topic_name; only the visible string upgrades.
                      const topicDisplay = tp.topic_display ?? topicName;
                      const isSel = unitSelectedTopics.includes(topicName);
                      return (
                        <div
                          key={ti}
                          onClick={() => toggleTopic(topicName, uid)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 4px', cursor: 'pointer', borderRadius: 8 }}
                        >
                          <div style={{
                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                            border: `2px solid ${isSel ? '#DC2626' : '#D1D5DB'}`,
                            background: isSel ? '#DC2626' : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {isSel && <span style={{ color: 'white', fontSize: 9, fontWeight: 800 }}>✓</span>}
                          </div>
                          <span style={{ fontSize: 12, fontFamily: "'Nunito',sans-serif", fontWeight: 700, color: isSel ? '#DC2626' : '#4B5563' }}>
                            {topicDisplay}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          };
          const isArabicSubject = ARABIC_ONLY_SUBJECTS.includes(subject?.subject_name);
          const hasSemesters = isArabicSubject && (unitList || []).some(u => u.semester != null);
          const unitsSem1 = hasSemesters ? unitList.filter(u => u.semester === 1) : (unitList || []);
          const unitsSem2 = hasSemesters ? unitList.filter(u => u.semester === 2) : [];
          const termHeaderStyle = { fontSize: 13, fontWeight: 700, color: '#1E3A5F', fontFamily: "'Sora',sans-serif", margin: '6px 0 8px 0' };
          if (!hasSemesters) return (unitList || []).map(renderUnitRow);
          return (
            <>
              <div style={termHeaderStyle}>{contentLang === 'ar' ? '📚 الفصل الأول' : '📚 Term 1'}</div>
              {unitsSem1.map(renderUnitRow)}
              {unitsSem2.length > 0 && (
                <>
                  <div style={{ ...termHeaderStyle, marginTop: 14 }}>{contentLang === 'ar' ? '📚 الفصل الثاني' : '📚 Term 2'}</div>
                  {unitsSem2.map(renderUnitRow)}
                </>
              )}
            </>
          );
        })()}
      </div>

      <p className="qh-ch-note">{t.challenge_note}</p>

      {error && <p className="qh-ut-error">{t.fail_load}</p>}

      {loading && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ textAlign: 'center', fontSize: 14, marginBottom: 6, direction: 'rtl' }}>
            {lang === 'ar' ? `جارى تحضير السؤال ${builtCount} من ${buildTotal || count}` : `Building question ${builtCount} of ${buildTotal || count}`}
          </p>
          <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#6366F1', borderRadius: 4, width: `${(buildTotal || count) > 0 ? Math.round((builtCount / (buildTotal || count)) * 100) : 0}%`, transition: 'width 0.3s ease' }} />
          </div>
        </div>
      )}
      <button
        className="qh-start-btn qh-ch-start-btn"
        disabled={selectedUnits.length === 0 || loading}
        onClick={handleStart}
      >
        {loading ? (lang === 'ar' ? 'جارى التجهيز...' : 'Building...') : t.start_challenge}
      </button>
    </div>
  );
}

/* ─── Subject quizzes view (View 2) ─────────────────────────────────────── */
const EXAM_TYPE_MAP = {
  unit:    'unit_test',
  weekly:  'weekly',
  monthly: 'monthly',
  midterm: 'mid_year',
  final:   'end_year',
};

function SubjectQuizzes({ subject, t, lang, onBack, preBuiltQuizzes, token }) {
  const navigate  = useNavigate();
  const types     = t.types;
  const unitList  = subject.units || [];
  const typeQuizzes = (preBuiltQuizzes || []).filter(q => q.subject_id === subject.subject_id);
  const displayName = lang === 'ar'
    ? (subject.subject_name_ar || subject.subject_name)
    : subject.subject_name;

  const [panelType, setPanelType] = useState(null);

  useEffect(() => {
    document.body.style.overflow = panelType ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [panelType]);

  const EXAM_TYPE_MAP = {
    unit:      'unit_test',
    weekly:    'weekly',
    monthly:   'monthly',
    midterm:   'mid_year',
    final:     'end_year',
    challenge: 'challenge',
    demand:    'demand',
  };

  const pill = (n, key) => `${n} ${t[key]}`;

  const TYPE_META = {
    unit:      { cls:'qh-t-unit',      icon:'📘', pills:[pill(30,'min'), pill(15,'questions')],  span:false },
    weekly:    { cls:'qh-t-weekly',    icon:'📅', pills:[pill(45,'min'), pill(20,'questions')],  span:false },
    monthly:   { cls:'qh-t-monthly',   icon:'🏅', pills:[pill(60,'min'), pill(25,'questions')],  span:false },
    midterm:   { cls:'qh-t-midyear',   icon:'📌', pills:[pill(90,'min'), pill(30,'questions')],  span:false },
    final:     { cls:'qh-t-endyear',   icon:'🏆', pills:[pill(120,'min'), pill(30,'questions')], span:false },
    challenge: { cls:'qh-t-challenge', icon:'⚡', pills:['×2 XP', pill(10,'questions')],         span:false },
    demand:    { cls:'qh-t-ondemand',  icon:'🎯', pills:[t.flexible, t.anyTime],                 span:false },
  };

  const openPanel  = (typeId) => setPanelType(typeId);
  const closePanel = () => setPanelType(null);
  const activeType = types.find(tp => tp.id === panelType);

  return (
    <div dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <StudentHeader title={displayName} showBack onBack={onBack} lang={lang} />

      <main className="qh-detail-main">
        {/* Bento grid */}
        <div className="qh-type-grid">
          {types.map(tp => {
            const meta = TYPE_META[tp.id] || { cls:'qh-t-unit', icon:'📋', pills:[], span:false };
            const quizCount = typeQuizzes.filter(q => q.exam_type === EXAM_TYPE_MAP[tp.id]).length;
            return (
              <div
                key={tp.id}
                className={`qh-qcard ${meta.cls}${meta.span ? ' qh-type-span2' : ''}`}
                onClick={() => openPanel(tp.id)}
              >
                <div className="qh-qcard-body">
                  <span className="qh-qcard-icon">{meta.icon}</span>
                  <div className="qh-qcard-name">{tp.label}</div>
                  <div className="qh-qcard-desc">{tp.desc || ''}</div>
                </div>
                <div className="qh-qcard-foot">
                  <div className="qh-qcard-pills">
                    {meta.pills.map((p, i) => (
                      <span key={i} className="qh-qcard-pill">{p}</span>
                    ))}
                    {quizCount > 0 && (
                      <span className="qh-qcard-pill">{quizCount} {lang === 'ar' ? 'متاح' : 'ready'}</span>
                    )}
                  </div>
                  <div className="qh-qcard-arrow">{lang === 'ar' ? '←' : '→'}</div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Slide-up panel overlay */}
      <div
        className={`qh-panel-overlay${panelType ? ' open' : ''}`}
        onClick={e => { if (e.target.classList.contains('qh-panel-overlay')) closePanel(); }}
      >
        <div className="qh-panel">
          <div className="qh-panel-drag" />
          {activeType && (() => {
            const meta        = TYPE_META[activeType.id] || {};
            const isDemand    = activeType.id === 'demand';
            const isChallenge = activeType.id === 'challenge';
            const tq = typeQuizzes.filter(q =>
              q.exam_type === EXAM_TYPE_MAP[activeType.id]
            );
            return (
              <>
                <div className="qh-panel-head">
                  <div className="qh-panel-icon" style={{ background: isDemand ? '#F0FDFA' : isChallenge ? '#FEF2F2' : '#EFF6FF' }}>
                    {meta.icon}
                  </div>
                  <div>
                    <div className="qh-panel-title">{activeType.label}</div>
                    <div className="qh-panel-sub">{(meta.pills || []).join(' · ')}</div>
                  </div>
                  <button className="qh-panel-close" onClick={closePanel}>✕</button>
                </div>

                <div className="qh-panel-body">
                  {isDemand ? (
                    <OnDemandForm
                      t={t}
                      unitList={unitList}
                      lang={lang}
                      subject={subject}
                      navigate={navigate}
                      token={token}
                    />
                  ) : isChallenge ? (
                    <ChallengeForm
                      t={t}
                      unitList={unitList}
                      lang={lang}
                      subject={subject}
                      navigate={navigate}
                      token={token}
                    />
                  ) : (
                    <>
                      {tq.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', fontFamily: "'Nunito',sans-serif", letterSpacing: '.06em', marginBottom: 8 }}>
                            {lang === 'ar' ? 'الاختبارات المتاحة' : 'AVAILABLE QUIZZES'}
                          </div>
                          {tq.map(quiz => (
                            <UnitTestCard
                              key={quiz.id}
                              quiz={quiz}
                              subject={subject}
                              token={token}
                              lang={lang}
                              t={t}
                              navigate={navigate}
                              unitList={unitList}
                              noCustomPanel={true}
                            />
                          ))}
                        </div>
                      )}
                      <UnitTestCard
                        key="custom-only"
                        quiz={{ id: null, exam_type: EXAM_TYPE_MAP[activeType.id] }}
                        subject={subject}
                        token={token}
                        lang={lang}
                        t={t}
                        navigate={navigate}
                        unitList={unitList}
                        noCustomPanel={false}
                      />
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/* ─── Subject emoji fallback ────────────────────────────────────────────── */
function getSubjectEmoji(name, nameAr) {
  const n = (name || '').toLowerCase();
  const a = (nameAr || '').toLowerCase();
  if (n.includes('math') || a.includes('رياضيات')) return '🔢';
  if (n.includes('science') || a.includes('علوم')) return '🔬';
  if (n.includes('physics') || a.includes('فيزياء')) return '⚡';
  if (n.includes('chemistry') || a.includes('كيمياء')) return '🧪';
  if (n.includes('biology') || a.includes('أحياء')) return '🌿';
  if (n.includes('english')) return '📖';
  if (n.includes('arabic') || a.includes('عربي') || a.includes('اللغة العربية')) return '✍️';
  if (n.includes('history') || a.includes('تاريخ')) return '🏛️';
  if (n.includes('geography') || a.includes('جغرافيا')) return '🌍';
  if (n.includes('computing') || n.includes('computer') || n.includes('ict')) return '💻';
  if (n.includes('islamic') || a.includes('إسلامية') || a.includes('التربية الإسلامية')) return '☪️';
  if (n.includes('global') || n.includes('perspective')) return '🌐';
  if (n.includes('foreign') || n.includes('modern foreign')) return '🗣️';
  if (a.includes('قطر') || n.includes('qatar') || a.includes('مواطنة')) return '🇶🇦';
  return '📚';
}

/* ─── Main component ────────────────────────────────────────────────────── */
export default function QuizHub() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const [lang,             setLang]             = useState('en');
  const [subjects,         setSubjects]         = useState([]);
  const [loadingSubjects,  setLoadingSubjects]  = useState(true);
  const [fetchError,       setFetchError]       = useState(null);
  const [selectedSubject,  setSelectedSubject]  = useState(null);
  const [preBuiltQuizzes,  setPreBuiltQuizzes]  = useState([]);
  const [showAnswers,      setShowAnswers]      = useState(false);
  const quizResult = location.state?.quiz_result || null;

  const studentId = (() => { try { return JSON.parse(localStorage.getItem('student_data'))?.id; } catch { return null; } })();
  const token     = localStorage.getItem('student_token');

  const t        = T[lang];
  const dir      = lang === 'ar' ? 'rtl' : 'ltr';
  const bodyFont = lang === 'ar' ? "'Noto Kufi Arabic', sans-serif" : "'Nunito', sans-serif";

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3]     = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t3 = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t3 += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t3 * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8)     pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3];     lp[li++] = pp[i * 3 + 1];     lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3];     lp[li++] = pp[j * 3 + 1];     lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3  - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!studentId || !token) { setLoadingSubjects(false); return; }
    studentFetch(`${API}/student/subjects/${studentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.success) {
          setSubjects(data.subjects || []);
          return studentFetch(`${API}/student/quizzes/${studentId}?include_completed=true`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then(r => r ? r.json() : null)
            .then(qdata => { if (qdata && qdata.success) setPreBuiltQuizzes(qdata.quizzes || []); })
            .catch(() => {});
        } else {
          setFetchError(true);
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoadingSubjects(false));
  }, []);

  const root = (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: bodyFont, direction: dir, position: 'relative' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{CSS}</style>
      {selectedSubject
        ? <SubjectQuizzes subject={selectedSubject} t={t} lang={lang} onBack={() => setSelectedSubject(null)} preBuiltQuizzes={preBuiltQuizzes} token={token} />
        : (
          <>
            <StudentHeader
              title={t.title}
              showBack
              onBack={() => navigate('/student/dashboard')}
              lang={lang}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '8px 20px' }}>
              {quizResult && (
                <button className="qh-answers-btn" onClick={() => setShowAnswers(s => !s)}>
                  {t.show_answers}
                </button>
              )}
              <button className="qh-lang-btn" onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')}>{t.toggle}</button>
            </div>

            {showAnswers && quizResult && (() => {
              const wrongAnswers = (quizResult.results || []).filter(r => r.isCorrect === false);
              return (
                <div className="qh-answers-wrap">
                  <div className="qh-answers-hdr">
                    <span className="qh-answers-title">{t.wrong_answers_title}</span>
                    <button className="qh-answers-close" onClick={() => setShowAnswers(false)}>{t.close}</button>
                  </div>
                  {wrongAnswers.length === 0
                    ? <p className="qh-perfect">{t.perfect_score}</p>
                    : wrongAnswers.map((r, i) => (
                        <div key={i} className="qh-wrong-card">
                          <p className="qh-wrong-q">{r.question_text}</p>
                          <p className="qh-wrong-ans">{t.your_answer} {r.answer}</p>
                          {r.justification && <p className="qh-wrong-just">{r.justification}</p>}
                        </div>
                      ))
                  }
                </div>
              );
            })()}

            <main className="qh-grid-main">
              {/* Loading */}
              {loadingSubjects && [0,1,2,3,4,5].map(i => (
                <div key={i} className="qh-subject-card" style={{ cursor: 'default' }}>
                  <div className="qh-skel" style={{ width: 56, height: 56, borderRadius: 16, marginBottom: 14 }} />
                  <div className="qh-skel" style={{ width: '65%', height: 18, borderRadius: 8, marginBottom: 10 }} />
                  <div className="qh-skel" style={{ width: '45%', height: 24, borderRadius: 20 }} />
                </div>
              ))}

              {/* Error */}
              {!loadingSubjects && fetchError && (
                <div className="qh-state-msg" style={{ gridColumn: '1/-1' }}>{t.fetch_error}</div>
              )}

              {/* Empty */}
              {!loadingSubjects && !fetchError && subjects.length === 0 && (
                <div className="qh-state-msg" style={{ gridColumn: '1/-1' }}>{t.no_subjects}</div>
              )}

              {/* Subject cards */}
              {!loadingSubjects && !fetchError && subjects.map(s => {
                const name = lang === 'ar' ? (s.subject_name_ar || s.subject_name) : s.subject_name;
                return (
                  <div
                    key={s.subject_id || s.subject_name}
                    className="qh-subject-card"
                    onClick={() => setSelectedSubject(s)}
                  >
                    <span className="qh-subject-emoji">{getSubjectEmoji(s.subject_name, s.subject_name_ar) || s.subject_emoji || '📚'}</span>
                    <span className="qh-subject-name">{name}</span>
                    <span className="qh-subject-pill">{t.quiz_types_pill}</span>
                  </div>
                );
              })}
            </main>
          </>
        )
      }
    </div>
  );

  return root;
}

/* ─── Styles ────────────────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700;800&family=Noto+Kufi+Arabic:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Header ── */
  .qh-header {
    background: rgba(255,255,255,0.85);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(229,231,235,0.8);
    padding: 16px 28px;
    display: flex; align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 100;
    box-shadow: 0 2px 16px rgba(0,0,0,.05);
  }
  .qh-logo { display: flex; align-items: center; }
  .qh-title {
    font-family: 'Sora', sans-serif;
    font-size: 22px; font-weight: 700; color: #1E3A5F;
    flex: 1; text-align: center;
  }
  .qh-header-actions { display: flex; align-items: center; gap: 8px; }
  .qh-back-btn {
    background: none; border: none;
    font-size: 13px; font-weight: 700; color: #6B7280;
    cursor: pointer; white-space: nowrap; font-family: inherit;
    padding: 6px 2px; transition: color .15s;
  }
  .qh-back-btn:hover { color: #1E3A5F; }
  .qh-lang-btn {
    background: #F0FDF4; border: 2px solid #D1FAE5;
    border-radius: 20px; padding: 5px 14px;
    font-size: 12px; font-weight: 800; color: #059669;
    cursor: pointer; font-family: 'Sora', sans-serif; transition: all .15s;
    box-shadow: 0 2px 0 #D1FAE5;
  }
  .qh-lang-btn:hover { background: #D1FAE5; border-color: #059669; }

  /* ── Grid (View 1) ── */
  .qh-grid-main {
    max-width: 1100px; margin: 0 auto;
    padding: 32px 40px 80px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    position: relative; z-index: 10;
  }
  @media (max-width: 767px) { .qh-grid-main { grid-template-columns: repeat(2,1fr); padding-bottom: 24px; } }
  @media (min-width: 768px) and (max-width: 1023px) { .qh-grid-main { grid-template-columns: repeat(3,1fr); } }
  @media (min-width: 1024px) { .qh-grid-main { grid-template-columns: repeat(4,1fr); } }

  /* ── Subject card ── */
  .qh-subject-card {
    background: white;
    border-radius: 20px;
    border: 1px solid #E5E7EB;
    box-shadow: 0 4px 20px rgba(5,150,105,.10);
    cursor: pointer;
    transition: transform .25s cubic-bezier(.34,1.56,.64,1),
                box-shadow .25s;
    overflow: hidden;
    aspect-ratio: unset;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .qh-subject-card:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 12px 32px rgba(5,150,105,.18);
  }
  .qh-subject-card .qh-subject-emoji {
    font-size: 36px;
    margin-bottom: 4px;
  }
  .qh-subject-card .qh-subject-name {
    font-size: 15px;
    font-weight: 800;
    color: #1E3A5F;
    font-family: 'Sora', sans-serif;
  }
  .qh-subject-card .qh-subject-pill {
    display: inline-flex;
    align-items: center;
    background: #ECFDF5;
    color: #059669;
    border-radius: 8px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Nunito', sans-serif;
    width: fit-content;
  }

  /* ── Detail view (View 2) ── */
  .qh-detail-main {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    padding: 24px 40px 80px;
    position: relative;
    z-index: 10;
    box-sizing: border-box;
  }
  @media (max-width: 768px) {
    .qh-detail-main { padding: 16px 16px 60px; }
  }

  /* ── Bento grid ── */
  .qh-type-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
  }
  @media (max-width: 640px) {
    .qh-type-grid { grid-template-columns: 1fr; }
    .qh-type-span2 { grid-column: span 1 !important; }
  }
  .qh-type-span2 { grid-column: span 2; }

  /* Base quiz type card */
  .qh-qcard {
    border-radius: 20px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: transform .3s cubic-bezier(.34,1.56,.64,1),
                box-shadow .3s;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    border: 1.5px solid transparent;
    padding: 0;
  }
  .qh-qcard:hover {
    transform: translateY(-5px) scale(1.015);
  }
  .qh-qcard-body {
    position: relative;
    z-index: 2;
    padding: 18px 18px 0;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .qh-qcard-icon {
    font-size: 26px;
    margin-bottom: 8px;
    display: block;
  }
  .qh-qcard-name {
    font-size: 15px;
    font-weight: 800;
    margin-bottom: 4px;
    font-family: 'Sora', sans-serif;
  }
  .qh-qcard-desc {
    font-size: 11px;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    opacity: .75;
    line-height: 1.5;
    flex: 1;
  }
  .qh-qcard-foot {
    position: relative;
    z-index: 2;
    padding: 10px 18px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
  }
  .qh-qcard-pills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .qh-qcard-pill {
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'Nunito', sans-serif;
    background: rgba(255,255,255,.25);
    border: 1px solid rgba(255,255,255,.3);
    color: inherit;
  }
  .qh-qcard-arrow {
    width: 30px;
    height: 30px;
    border-radius: 10px;
    background: rgba(255,255,255,.25);
    border: 1px solid rgba(255,255,255,.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: .2s;
    flex-shrink: 0;
  }
  .qh-qcard:hover .qh-qcard-arrow {
    background: rgba(255,255,255,.45);
    transform: translateX(-3px);
  }

  /* Color themes */
  .qh-t-unit {
    background: linear-gradient(145deg,#1E3A8A,#1D4ED8,#3B82F6);
    color: white;
    box-shadow: 0 6px 24px rgba(29,78,216,.25);
  }
  .qh-t-unit:hover { box-shadow: 0 14px 36px rgba(29,78,216,.4); }

  .qh-t-weekly {
    background: linear-gradient(145deg,#4C1D95,#6D28D9,#8B5CF6);
    color: white;
    box-shadow: 0 6px 24px rgba(109,40,217,.25);
  }
  .qh-t-weekly:hover { box-shadow: 0 14px 36px rgba(109,40,217,.4); }

  .qh-t-monthly {
    background: linear-gradient(145deg,#78350F,#D97706,#F59E0B);
    color: white;
    box-shadow: 0 6px 24px rgba(217,119,6,.28);
  }
  .qh-t-monthly:hover { box-shadow: 0 14px 36px rgba(217,119,6,.42); }

  .qh-t-midyear {
    background: linear-gradient(145deg,#7C2D12,#C2410C,#F97316);
    color: white;
    box-shadow: 0 6px 24px rgba(194,65,12,.28);
  }
  .qh-t-midyear:hover { box-shadow: 0 14px 36px rgba(194,65,12,.42); }

  .qh-t-endyear {
    background: linear-gradient(145deg,#1E1B4B,#4338CA,#6366F1);
    color: white;
    box-shadow: 0 6px 24px rgba(67,56,202,.3);
  }
  .qh-t-endyear:hover { box-shadow: 0 14px 36px rgba(67,56,202,.45); }

  .qh-t-challenge {
    background: linear-gradient(145deg,#450A0A,#991B1B,#EF4444);
    color: white;
    box-shadow: 0 6px 24px rgba(153,27,27,.3);
  }
  .qh-t-challenge:hover { box-shadow: 0 14px 36px rgba(153,27,27,.45); }

  .qh-t-ondemand {
    background: linear-gradient(145deg,#134E4A,#0D9488,#14B8A6);
    color: white;
    box-shadow: 0 6px 24px rgba(13,148,136,.28);
  }
  .qh-t-ondemand:hover { box-shadow: 0 14px 36px rgba(13,148,136,.42); }

  /* Slide-up panel overlay */
  .qh-panel-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,.45);
    backdrop-filter: blur(6px);
    z-index: 200;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity .3s;
  }
  .qh-panel-overlay.open {
    opacity: 1;
    pointer-events: all;
  }
  .qh-panel {
    background: white;
    border-radius: 24px 24px 0 0;
    width: 100%;
    max-width: 860px;
    max-height: 85vh;
    overflow-y: auto;
    padding-bottom: 32px;
    transform: translateY(100%);
    transition: transform .4s cubic-bezier(.34,1.2,.64,1);
  }
  .qh-panel-overlay.open .qh-panel {
    transform: translateY(0);
  }
  .qh-panel-drag {
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: #E5E7EB;
    margin: 12px auto 0;
  }
  .qh-panel-head {
    padding: 16px 20px 12px;
    border-bottom: 1px solid #F3F4F6;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .qh-panel-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
  }
  .qh-panel-title {
    font-size: 18px;
    font-weight: 800;
    color: #1E3A5F;
    font-family: 'Sora', sans-serif;
  }
  .qh-panel-sub {
    font-size: 12px;
    color: #6B7280;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    margin-top: 2px;
  }
  .qh-panel-close {
    margin-left: auto;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #6B7280;
    transition: .2s;
    flex-shrink: 0;
  }
  .qh-panel-close:hover {
    background: #FEE2E2;
    border-color: #FCA5A5;
    color: #DC2626;
  }
  .qh-panel-body {
    padding: 16px 20px;
  }
  .qh-panel-cta {
    padding: 12px 20px 0;
    display: flex;
    gap: 10px;
  }
  .qh-btn-start {
    flex: 1;
    padding: 14px;
    background: linear-gradient(135deg,#059669,#10B981);
    color: white;
    border: none;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 800;
    font-family: 'Sora', sans-serif;
    cursor: pointer;
    transition: .2s;
    box-shadow: 0 4px 14px rgba(5,150,105,.28);
  }
  .qh-btn-start:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(5,150,105,.38);
  }
  .qh-btn-start:disabled {
    opacity: .4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    background: #9CA3AF;
  }
  .qh-btn-custom {
    padding: 14px 18px;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 14px;
    font-size: 13px;
    font-weight: 700;
    font-family: 'Sora', sans-serif;
    color: #1E3A5F;
    cursor: pointer;
    transition: .2s;
    white-space: nowrap;
  }
  .qh-btn-custom:hover {
    background: #ECFDF5;
    border-color: #059669;
    color: #059669;
  }

  /* Quiz item rows inside panel */
  .qh-qitem {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 0;
    cursor: pointer;
    border-top: 1px solid #F9FAFB;
    transition: .15s;
    border-radius: 10px;
    padding-inline: 8px;
  }
  .qh-qitem:hover { background: #F9FAFB; }
  .qh-qitem-num {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 800;
    font-family: 'Nunito', sans-serif;
    flex-shrink: 0;
  }
  .qh-qitem-name {
    font-size: 13px;
    font-weight: 700;
    color: #1E3A5F;
    font-family: 'Sora', sans-serif;
  }
  .qh-qitem-meta {
    font-size: 11px;
    color: #9CA3AF;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    margin-top: 2px;
  }
  .qh-qitem-arrow {
    color: #D1D5DB;
    margin-left: auto;
    font-size: 13px;
    transition: .2s;
  }
  .qh-qitem:hover .qh-qitem-arrow {
    color: #059669;
    transform: translateX(-3px);
  }

  @keyframes qhCardIn {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .qh-qcard { animation: qhCardIn .4s ease both; }
  .qh-qcard:nth-child(1) { animation-delay: .04s; }
  .qh-qcard:nth-child(2) { animation-delay: .1s; }
  .qh-qcard:nth-child(3) { animation-delay: .16s; }
  .qh-qcard:nth-child(4) { animation-delay: .22s; }
  .qh-qcard:nth-child(5) { animation-delay: .28s; }
  .qh-qcard:nth-child(6) { animation-delay: .34s; }
  .qh-qcard:nth-child(7) { animation-delay: .40s; }

  /* ── On-demand wrapper (kept for inner forms) ── */
  .qh-demand-wrap {
    overflow: visible;
  }

  /* ── On-demand form ── */
  .qh-od-form {
    display: flex; flex-direction: column; gap: 20px;
    padding: 20px;
    background: rgba(13,148,136,.04);
    border: 1px solid rgba(13,148,136,.15);
    border-top: none;
    border-radius: 0 0 16px 16px;
  }
  .qh-od-field { display: flex; flex-direction: column; gap: 10px; }
  .qh-od-label {
    font-size: 13px; font-weight: 600; color: #374151;
    display: flex; align-items: center; gap: 8px;
  }
  .qh-count-pill {
    background: rgba(13,148,136,.12); color: #0D9488;
    font-size: 12px; font-weight: 800;
    padding: 2px 10px; border-radius: 20px;
  }
  .qh-units-list { display: flex; flex-direction: column; gap: 6px; }
  .qh-unit-row {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 13px; border-radius: 14px;
    border: 2px solid #E5E7EB; background: white;
    cursor: pointer;
    box-shadow: 0 2px 0 #E5E7EB;
    transition: transform .1s, box-shadow .1s, border-color .1s;
  }
  .qh-unit-row:hover {
    border-color: #0D9488;
    box-shadow: 0 2px 0 rgba(13,148,136,.3);
    transform: translateY(-2px);
  }
  .qh-unit-checked {
    border-color: #0D9488; background: rgba(13,148,136,.04);
    box-shadow: 0 2px 0 rgba(13,148,136,.3);
    transform: translateY(-2px);
  }
  .qh-checkbox { accent-color: #0D9488; width: 15px; height: 15px; cursor: pointer; flex-shrink: 0; }
  .qh-unit-name { font-size: 13px; font-weight: 600; color: #374151; }
  .qh-slider { width: 100%; accent-color: #0D9488; cursor: pointer; }
  .qh-slider-ends {
    display: flex; justify-content: space-between;
    font-size: 11px; color: #9CA3AF; font-weight: 600; margin-top: 2px;
  }
  .qh-diff-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .qh-diff-btn {
    flex: 1; min-width: 60px; padding: 8px 6px; border-radius: 10px;
    border: 1.5px solid #D1D5DB; background: white;
    font-family: inherit; font-size: 12px; font-weight: 700;
    color: #6B7280; cursor: pointer; transition: all .15s; text-align: center;
  }
  .qh-diff-btn:hover { border-color: #0D9488; color: #0D9488; }
  .qh-diff-active { background: #0D9488; border-color: #0D9488; color: white; }
  .qh-start-btn {
    width: 100%; padding: 12px;
    background: #D1FAE5;
    border: 2px solid #6EE7B7; border-radius: 18px;
    font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 900;
    color: #065F46; cursor: pointer;
    box-shadow: 0 5px 0 #6EE7B7, 0 8px 16px rgba(5,150,105,.12);
    transition: transform .08s, box-shadow .08s;
  }
  .qh-start-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 0 #6EE7B7, 0 12px 22px rgba(5,150,105,.2);
  }
  .qh-start-btn:active:not(:disabled) {
    transform: translateY(4px);
    box-shadow: 0 1px 0 #6EE7B7;
  }
  .qh-start-btn:disabled { background: #D1D5DB; border-color: #D1D5DB; box-shadow: none; cursor: not-allowed; color: #9CA3AF; }
  .qh-ch-start-btn {
    background: #FEE2E2;
    border: 2px solid #FECACA; border-radius: 18px;
    color: #991B1B; font-weight: 900;
    box-shadow: 0 5px 0 #FECACA, 0 8px 16px rgba(220,38,38,.12);
    transition: transform .08s, box-shadow .08s;
  }
  .qh-ch-start-btn:hover:not(:disabled) {
    box-shadow: 0 5px 0 #FECACA, 0 12px 22px rgba(220,38,38,.2);
  }
  .qh-ch-start-btn:active:not(:disabled) {
    transform: translateY(4px);
    box-shadow: 0 1px 0 #FECACA;
  }
  .qh-ch-note {
    font-size: 12px; font-weight: 600; color: #9CA3AF;
    font-style: italic;
  }

  /* ── Count badge (unit test) ── */
  .qh-count-badge {
    font-size: 12px; font-weight: 800;
    padding: 2px 10px; border-radius: 20px;
  }

  /* ── Unit test quiz card ── */
  .qh-ut-card {
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
    background: rgba(29,78,216,.04);
    border: 2px solid #DBEAFE;
    border-radius: 18px;
    padding: 14px 18px;
    margin-bottom: 10px;
    box-shadow: 0 3px 0 #DBEAFE, 0 6px 16px rgba(29,78,216,.08);
  }
  .qh-ut-info { flex: 1; display: flex; flex-direction: column; gap: 3px; }
  .qh-ut-unit {
    font-family: 'Sora', sans-serif;
    font-size: 14px; font-weight: 700; color: #1D4ED8;
  }
  .qh-ut-meta { font-size: 12px; font-weight: 600; color: #6B7280; }
  .qh-ut-start {
    background: #059669; border: 2px solid #6EE7B7; border-radius: 20px;
    padding: 7px 20px; font-family: 'Sora', sans-serif;
    font-size: 13px; font-weight: 700; color: white;
    cursor: pointer; white-space: nowrap;
    box-shadow: 0 3px 0 #6EE7B7;
    transition: transform .08s, box-shadow .08s;
  }
  .qh-ut-start:hover:not(:disabled) { background: #047857; transform: translateY(-1px); }
  .qh-ut-start:active:not(:disabled) { transform: translateY(3px); box-shadow: 0 1px 0 #6EE7B7; }
  .qh-ut-start:disabled { background: #D1D5DB; border-color: #D1D5DB; box-shadow: none; cursor: not-allowed; }
  .qh-ut-done {
    background: #D1FAE5; color: #065F46;
    border: 1.5px solid #6EE7B7; border-radius: 20px;
    padding: 7px 16px; font-family: 'Sora', sans-serif;
    font-size: 13px; font-weight: 700; white-space: nowrap;
  }
  .qh-ut-error {
    width: 100%; font-size: 12px; font-weight: 600;
    color: #DC2626; margin-top: 2px;
  }

  /* ── Show Answers button ── */
  .qh-answers-btn {
    background: #FEF2F2; border: 1.5px solid rgba(220,38,38,.25);
    border-radius: 20px; padding: 5px 14px;
    font-size: 12px; font-weight: 800; color: #DC2626;
    cursor: pointer; font-family: 'Sora', sans-serif; transition: all .15s;
  }
  .qh-answers-btn:hover { background: #FEE2E2; border-color: #DC2626; }

  /* ── Wrong-answers panel ── */
  .qh-answers-wrap {
    max-width: 680px; margin: 0 auto;
    padding: 20px 24px;
    display: flex; flex-direction: column; gap: 12px;
  }
  .qh-answers-hdr {
    display: flex; align-items: center; justify-content: space-between;
  }
  .qh-answers-title {
    font-family: 'Sora', sans-serif;
    font-size: 16px; font-weight: 700; color: #1E3A5F;
  }
  .qh-answers-close {
    background: none; border: none;
    font-size: 13px; font-weight: 700; color: #9CA3AF;
    cursor: pointer; font-family: inherit; transition: color .15s;
  }
  .qh-answers-close:hover { color: #1E3A5F; }
  .qh-perfect {
    font-size: 14px; font-weight: 600; color: #059669;
    text-align: center; padding: 20px 0;
  }
  .qh-wrong-card {
    background: #FEF2F2; border: 1px solid rgba(220,38,38,.18);
    border-radius: 12px; padding: 14px 18px;
    display: flex; flex-direction: column; gap: 6px;
  }
  .qh-wrong-q  { font-size: 14px; font-weight: 600; color: #1E3A5F; }
  .qh-wrong-ans  { font-size: 13px; font-weight: 700; color: #DC2626; }
  .qh-wrong-just { font-size: 12px; font-weight: 500; color: #6B7280; font-style: italic; }

  /* ── State messages ── */
  .qh-state-msg {
    text-align: center; padding: 64px 24px;
    font-size: 15px; font-weight: 600; color: #9CA3AF;
    font-family: 'Sora', sans-serif;
  }

  /* ── Skeleton ── */
  @keyframes qh-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .qh-skel { background: #E5E7EB; animation: qh-pulse 1.5s ease-in-out infinite; }

  /* ── Topic selection ── */
  .qh-topic-group { display: flex; flex-direction: column; gap: 6px; }
  .qh-topic-group-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 4px 2px;
  }
  .qh-topic-group-name { font-size: 12px; font-weight: 700; color: #6B7280; }
  .qh-select-all-btn {
    background: none; border: none; font-family: inherit;
    font-size: 11px; font-weight: 700; color: #0D9488;
    cursor: pointer; padding: 2px 6px; border-radius: 8px;
    transition: background .12s;
  }
  .qh-select-all-btn:hover { background: rgba(13,148,136,.08); }
  .qh-topics-list { display: flex; flex-direction: column; gap: 5px; padding-left: 8px; }

  /* ── UnitTestCard custom quiz ── */
  .qh-ut-custom-btn {
    width: 100%; margin-top: 4px;
    background: none; border: 1.5px solid #DBEAFE;
    border-radius: 12px; padding: 7px 14px;
    font-family: inherit; font-size: 12px; font-weight: 700;
    color: #1D4ED8; cursor: pointer; transition: all .15s; text-align: center;
  }
  .qh-ut-custom-btn:hover { background: rgba(29,78,216,.04); border-color: #1D4ED8; }
  .qh-custom-wrap {
    background: rgba(29,78,216,.02);
    border: 2px solid #DBEAFE; border-top: none;
    border-radius: 0 0 18px 18px;
    padding: 18px;
    display: flex; flex-direction: column; gap: 14px;
    margin-bottom: 10px;
  }

  /* ── Responsive ── */
  @media (max-width: 600px) {
    .qh-header  { padding: 13px 16px; }
    .qh-title   { font-size: 18px; }
    .qh-detail-main { padding: 20px 14px 80px; }
    .qh-type-card   { padding: 16px 18px; }
  }
`;
